import moment from "moment";
import React, { type CSSProperties, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import getCurrencySymbol from "../../utils/getCurrencySymbol";
import SafeECharts from "./SafeECharts";

// Local type definitions
interface DataPoint {
  timestamp?: string | number;
  absoluteProfit?: number | null;
  drawdown?: number | null;
}

interface BotPerformanceGraphProps {
  data?: DataPoint[];
  currency?: string;
  stopLoss?: number;
  sailingMode?: number | string | false;
  isLoading?: boolean;
  style?: CSSProperties;
  className?: string;
  [key: string]: unknown;
}

const BotPerformanceGraph: React.FC<BotPerformanceGraphProps> = ({
  data = [],
  currency,
  stopLoss,
  sailingMode,
  isLoading = false,
  style,
  className,
  ...EChartsProps
}) => {
  const { i18n, t } = useTranslation("stats", { keyPrefix: "performanceGraph" });
  const symbol = useMemo(() => getCurrencySymbol(currency), [currency]);

  if (isLoading) return <Skeleton height={style?.height || 400} />;

  const showDrawdown = data?.[0]?.drawdown != null;

  const lastDrawdown = data?.[data.length - 1]?.drawdown;
  const sailingModeIsActive =
    showDrawdown && sailingMode && lastDrawdown != null && lastDrawdown > parseFloat(String(sailingMode));

  const profitColor = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
  const drawdownColor = getComputedStyle(document.documentElement).getPropertyValue("--default").trim();
  const sailingModeColor = getComputedStyle(document.documentElement).getPropertyValue("--success").trim();
  const stopLossColor = getComputedStyle(document.documentElement).getPropertyValue("--danger").trim();
  return (
    <SafeECharts
      {...EChartsProps}
      className={className}
      style={style}
      option={{
        dataset: [
          {
            dimensions: ["timestamp", "absoluteProfit", "drawdown"],
            source: data,
          },
        ],
        grid: [
          {
            left: 0,
            containLabel: true,
            bottom: 0,
            top: 0,
            right: 0,
            height: "75%",
          },
          {
            left: 0,
            containLabel: true,
            bottom: 0,
            right: 0,
            height: "25%",
          },
        ],
        tooltip: {
          trigger: "axis",
          axisPointer: {
            label: {
              formatter: ({ value }: { value: string | number }) =>
                moment(value).locale(i18n.language).format("DD/MM/YYYY HH:mm"),
            },
          },
        },
        xAxis: [
          {
            type: "time",
            axisLabel: {
              padding: [6, 0, 0, 0],
              margin: 6,
            },
            axisLine: { onZero: true },
            hideOverlap: true,
          },
          {
            gridIndex: 1,
            type: "time",
            axisLabel: {
              padding: [0, 0, 6, 0],
              margin: 5,
            },
            axisLine: { onZero: true },
            position: "top",
            hideOverlap: true,
          },
        ],
        yAxis: [
          {
            gridIndex: 0,
            min: ({ min, max }: { min: number; max: number }) => {
              const porcentualMin = Math.floor(min - (max - min) * 0.1);
              return min < 0 ? porcentualMin : Math.max(porcentualMin, 0);
            },
            axisLabel: {
              inside: true,
              verticalAlign: "top",
              padding: [3, 0, 0, 0],
              formatter: `{value} ${symbol}`,
            },
            minInterval: 1,
            splitNumber: 3,
            splitLine: {
              lineStyle: {
                opacity: 0.35,
              },
            },
          },
          {
            gridIndex: 1,
            show: showDrawdown,
            minInterval: 1,
            inverse: true,
            min: 0,
            max: 100,
            splitNumber: 2,
            axisLabel: {
              inside: true,
              verticalAlign: "bottom",
              formatter: "{value} %",
            },
            splitLine: {
              lineStyle: {
                opacity: 0.35,
              },
            },
          },
        ],
        series: [
          {
            color: profitColor,
            type: "line",
            areaStyle: {
              opacity: 0.1,
            },
            name: t("profit", { symbol }),
            step: "end",
            showSymbol: false,
            yAxisIndex: 0,
            xAxisIndex: 0,
            encode: {
              x: "timestamp",
              y: "absoluteProfit",
            },
            tooltip: {
              valueFormatter: (value: number = 0) => `${value.toFixed(2)} ${symbol}`,
            },
          },
          {
            color: drawdownColor,
            type: "line",
            areaStyle: {
              opacity: 0.1,
            },
            name: t("drawdown"),
            smooth: 0.2,
            step: false,
            showSymbol: false,
            yAxisIndex: 1,
            xAxisIndex: 1,
            encode: {
              x: "timestamp",
              y: "drawdown",
            },
            tooltip: {
              show: showDrawdown,
              valueFormatter: (value: number = 0) => `${value.toFixed(2)} %`,
            },
            markLine: {
              silent: true,
              symbol: ["none", "none"],
              lineStyle: {
                width: 2,
              },
              data: [
                ...(sailingMode
                  ? [
                      {
                        // x: '50',
                        yAxis: sailingMode,
                        lineStyle: {
                          color: sailingModeColor,
                          opacity: sailingModeIsActive ? null : 0.25,
                        },
                      },
                    ]
                  : []),
                ...(stopLoss
                  ? [
                      {
                        // x: '50',
                        yAxis: stopLoss * 100,
                        lineStyle: {
                          color: stopLossColor,
                          type: "solid",
                          opacity: 0.6,
                        },
                      },
                    ]
                  : []),
              ],
            },
          },
        ],
      }}
    />
  );
};

export default BotPerformanceGraph;
